import { motion } from "framer-motion";
import { Zap, Sparkles, Shield, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";


const WhyChooseUsSection = () => {
  const navigate = useNavigate();
  
  const features = [
    {
      icon: Zap,
      title: "Flexible Terms",
      description: "Choose your plan. Choose pay-as-you-go access—hourly, daily, weekly, or monthly."
    },
    {
      icon: Sparkles,
      title: "Premium Setup",
      description: "Modern, fully furnished spaces designed for productivity."
    },
    {
      icon: Zap,
      title: "Fast Internet",
      description: "Fast, dependable Wi-Fi for calls, collaboration, and focused work."
    },
    {
      icon: Shield,
      title: "Meeting Rooms",
      description: "Fully equipped, professional meeting rooms—book when you need them."
    }
  ];

  const stats = [
    { value: "24/7", label: "Support" },
    { value: "100%", label: "Furnished" },
    { value: "Fast", label: "WiFi" }
  ];

  const companyState = useSelector((state) => state.companyReducer.company);
  const companyName = companyState?.name || "Our Company";

  return (
    <section className="relative py-20 bg-gradient-to-b from-white to-slate-50 overflow-hidden">
      {/* Background Effects */}
      <div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(circle at 30% 20%, var(--flex-primary-soft, rgba(59,130,246,0.05)), transparent 50%)' }}
      />
      <div
        className="absolute inset-0"
        style={{ background: 'radial-gradient(circle at 70% 80%, var(--flex-accent-soft, rgba(99,102,241,0.05)), transparent 50%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-md border border-blue-200/50 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-6"
            >
              <Sparkles className="w-4 h-4" />
              Why Choose Us
            </motion.div>

            {/* Heading */}
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
              Premium Workspaces
              <br />
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Designed for Success
              </span>
            </h2>

            {/* Description */}
            <p className="text-lg text-slate-600 mb-10 leading-relaxed">
              {companyName} offers modern, fully-equipped office spaces designed to meet the needs of professionals and growing businesses. We provide an environment that fosters productivity and creativity.
            </p>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -4 }}
                    className="group bg-white/70 backdrop-blur-md border border-white/60 rounded-xl p-4 hover:border-blue-300/60 hover:bg-white/85 hover:shadow-lg transition-all cursor-default"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-50/80 backdrop-blur-sm rounded-lg group-hover:bg-blue-100/80 transition-colors">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 mb-1">{feature.title}</h3>
                        {/*  Fixed: use feature.description instead of companyState?.description */}
                        <p className="text-sm text-slate-600">{feature.description}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* CTA Button */}
            <motion.button
              onClick={() => navigate('/flex-home')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-blue-600/30 transition-all"
            >
              Explore Our Spaces
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </motion.div>

          {/* Right Content - Stats & Visual */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            {/* Main Card */}
            <div className="relative bg-gradient-to-br from-blue-600/90 to-indigo-700/90 backdrop-blur-md rounded-3xl p-12 shadow-2xl shadow-blue-600/25 overflow-hidden border border-white/15">
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

              <div className="relative">
                <div className="text-white mb-8">
                  <h3 className="text-3xl font-bold mb-2">Built for Growth</h3>
                  <p className="text-blue-100">Empowering businesses to thrive</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-6 bg-white/5 backdrop-blur-sm rounded-2xl p-4">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.5 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + index * 0.1, type: "spring" }}
                      className="text-center"
                    >
                      <motion.div
                        whileHover={{ scale: 1.1 }}
                        className="text-4xl font-bold text-white mb-2"
                      >
                        {stat.value}
                      </motion.div>
                      <div className="text-sm text-blue-100 font-medium">
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Bottom Features */}
                <div className="mt-10 pt-8 border-t border-white/20">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      <span className="text-sm font-medium">Active Today</span>
                    </div>
                    <div className="flex items-center gap-3 text-white">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
                      <span className="text-sm font-medium">Fast Support</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              animate={{ y: [0, -8, 0] }}
              className="absolute -bottom-6 -left-6 bg-white/75 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-white/60"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-50 rounded-xl">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">Secure</div>
                  <div className="text-sm text-slate-600">Verified & Safe</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
